import type { Block } from '../types/proposal-blocks';

interface PdfGenerationInput {
  title: string;
  // Aceptamos cualquier shape de content porque el editor v1 (pages[]) fue
  // deprecado y todo va por v2 (blocks[]). Si llega v1, hacemos best-effort
  // sacando los strings visibles para no perder el signed PDF.
  content: any;
  acceptedByName: string;
  acceptedByEmail: string;
  acceptedAt: string;
  signatureData?: string;
}

const inlineMd = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

function renderBlock(block: Block): string {
  switch (block.type) {
    case 'hero':
      return `
        <div style="margin: 20px 0 32px;">
          ${block.eyebrow ? `<div style="font-size:10px;color:#888;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px;">${block.eyebrow}</div>` : ''}
          <h2 style="font-size:22px;font-weight:700;color:#0a0a0a;margin:0 0 4px;">${block.title}</h2>
          ${block.subtitle ? `<p style="font-size:13px;color:#666;margin:0;">${block.subtitle}</p>` : ''}
        </div>`;

    case 'section':
      return `
        <div style="margin: 20px 0;">
          <h3 style="font-size:16px;font-weight:600;color:#1a1a1a;margin:0 0 4px;">${block.title}</h3>
          ${block.subtitle ? `<p style="font-size:12px;color:#888;margin:0 0 12px;">${block.subtitle}</p>` : ''}
          <div style="font-size:12px;color:#444;line-height:1.6;white-space:pre-wrap;">${inlineMd(block.body_md || '')}</div>
        </div>`;

    case 'numbered_cards':
      return `
        <div style="margin: 20px 0;">
          ${block.cards.map(c => `
            <div style="display:flex;gap:12px;padding:12px;border:1px solid #e5e5e5;border-radius:6px;margin-bottom:8px;">
              <div style="font-size:24px;font-weight:700;color:#B6FF3C;min-width:40px;">${c.number}</div>
              <div>
                <div style="font-weight:600;font-size:13px;margin-bottom:4px;">${c.title}</div>
                <div style="font-size:11px;color:#555;line-height:1.5;">${c.description}</div>
              </div>
            </div>`).join('')}
        </div>`;

    case 'package_page':
      return `
        <div style="margin: 24px 0; padding:16px; border:2px solid ${block.badge === 'recomendado' ? '#22c55e' : '#e5e5e5'}; border-radius:8px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;">
            <h3 style="font-size:16px;font-weight:700;margin:0;">${block.name}${block.badge ? ` <span style="font-size:10px;text-transform:uppercase;background:#B6FF3C;padding:2px 6px;border-radius:100px;color:#0a0a0a;">${block.badge}</span>` : ''}</h3>
            <div style="text-align:right;">
              ${block.price ? `<div style="font-weight:700;font-size:16px;">${block.price}</div>` : ''}
              ${block.hours ? `<div style="font-size:11px;color:#666;">${block.hours}</div>` : ''}
            </div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            ${block.tasks.map(t => `
              <tr>
                <td style="padding:6px 8px;border-bottom:1px solid #eee;">
                  <div style="font-weight:600;">${t.name}</div>
                  <div style="color:#666;font-size:10px;">${t.description}</div>
                </td>
                <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;color:#666;white-space:nowrap;">${t.hours || ''}</td>
              </tr>`).join('')}
          </table>
          ${block.why ? `<div style="margin-top:12px;font-size:11px;color:#555;"><strong>Por qué:</strong> ${block.why}</div>` : ''}
        </div>`;

    case 'comparison_table':
      return `
        <div style="margin:24px 0;page-break-inside:avoid;">
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <thead><tr>
              <th style="background:#f5f5f5;border:1px solid #e5e5e5;padding:8px;"></th>
              ${block.columns.map(c => `<th style="background:${c.recommended ? '#f0fdf4' : '#f5f5f5'};border:1px solid #e5e5e5;padding:8px;font-weight:600;">${c.label}${c.recommended ? ' ★' : ''}</th>`).join('')}
            </tr></thead>
            <tbody>${block.rows.map(r => `
              <tr>
                <td style="border:1px solid #e5e5e5;padding:6px 8px;font-weight:600;font-size:10px;">${r.label}</td>
                ${r.values.map((v, i) => `<td style="border:1px solid #e5e5e5;padding:6px 8px;${block.columns[i]?.recommended ? 'background:#f0fdf4;' : ''}">${String(v)}</td>`).join('')}
              </tr>`).join('')}</tbody>
          </table>
          ${block.footnote ? `<p style="font-size:10px;color:#888;margin:6px 0 0;">${block.footnote}</p>` : ''}
        </div>`;

    case 'milestones':
      return `
        <div style="margin:24px 0;">
          <div style="display:flex;gap:12px;margin-bottom:16px;">
            ${block.metrics.map(m => `
              <div style="flex:1;padding:10px;background:#f9fafb;border-radius:6px;text-align:center;">
                <div style="font-size:18px;font-weight:700;">${m.value}</div>
                <div style="font-size:10px;color:#666;text-transform:uppercase;">${m.label}</div>
              </div>`).join('')}
          </div>
          ${block.items.map(it => `
            <div style="padding:10px;border-left:3px solid ${it.status === 'entregado' ? '#22c55e' : it.status === 'en_curso' ? '#f59e0b' : '#e5e5e5'};background:#fafafa;margin-bottom:6px;">
              <div style="font-weight:600;font-size:12px;">${it.title}</div>
              <div style="font-size:10px;color:#666;">${it.date || ''}${it.amount ? ` · ${it.amount}` : ''}</div>
            </div>`).join('')}
        </div>`;

    case 'table':
      return `
        <table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:11px;">
          ${block.caption ? `<caption style="font-size:11px;color:#666;text-align:left;margin-bottom:4px;">${block.caption}</caption>` : ''}
          <thead><tr>${block.headers.map(h => `<th style="background:#f5f5f5;border:1px solid #e5e5e5;padding:6px 10px;text-align:left;font-weight:600;">${h}</th>`).join('')}</tr></thead>
          <tbody>${block.rows.map(row => `<tr>${row.map(cell => `<td style="border:1px solid #e5e5e5;padding:6px 10px;">${cell}</td>`).join('')}</tr>`).join('')}</tbody>
        </table>`;

    case 'chart':
      // Chart rendering en PDF requiere canvas. Simplificamos a tabla de datos.
      return `
        <div style="margin:16px 0;">
          <div style="font-weight:600;font-size:12px;margin-bottom:6px;">${block.title}</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            ${block.data.map(d => `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee;">${d.label}</td><td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:600;">${d.value}</td></tr>`).join('')}
          </table>
        </div>`;

    case 'image':
      return `
        <figure style="margin:16px 0;text-align:center;">
          <img src="${block.src}" alt="${block.alt}" style="max-width:100%;border-radius:6px;" />
          ${block.caption ? `<figcaption style="font-size:10px;color:#888;margin-top:6px;">${block.caption}</figcaption>` : ''}
        </figure>`;

    case 'clauses':
      return `
        <div style="margin:20px 0;">
          <h3 style="font-size:14px;font-weight:600;margin:0 0 12px;">${block.title}</h3>
          ${block.items.map(it => `
            <div style="margin-bottom:12px;">
              <div style="font-weight:600;font-size:12px;">${it.number}. ${it.title}</div>
              <div style="font-size:11px;color:#444;line-height:1.5;margin-top:4px;white-space:pre-wrap;">${inlineMd(it.body_md)}</div>
            </div>`).join('')}
        </div>`;

    case 'code_block':
      return `
        <pre style="background:#f5f5f5;border:1px solid #e5e5e5;border-radius:4px;padding:10px;font-family:monospace;font-size:10px;overflow-x:auto;margin:12px 0;">${block.caption ? `<div style="font-size:10px;color:#666;margin-bottom:6px;">${block.caption}</div>` : ''}${block.code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;

    case 'callout': {
      const colors = {
        info: { bg: '#eff6ff', border: '#3b82f6' },
        tip: { bg: '#f0fdf4', border: '#22c55e' },
        warning: { bg: '#fef2f2', border: '#ef4444' },
        recommendation: { bg: '#fefce8', border: '#eab308' },
      }[block.variant] || { bg: '#f5f5f5', border: '#888' };
      return `
        <div style="background:${colors.bg};border-left:3px solid ${colors.border};padding:10px 14px;margin:12px 0;font-size:12px;color:#444;border-radius:0 4px 4px 0;">
          ${block.title ? `<div style="font-weight:600;margin-bottom:4px;">${block.title}</div>` : ''}
          <div style="white-space:pre-wrap;">${inlineMd(block.body_md)}</div>
        </div>`;
    }

    case 'conditions':
      return `
        <div style="margin:20px 0;padding:14px;background:#fafafa;border:1px solid #e5e5e5;border-radius:6px;font-size:11px;color:#444;line-height:1.5;white-space:pre-wrap;">${inlineMd(block.markdown)}</div>`;

    default:
      return '';
  }
}

class ProposalPdfService {
  async generateSignedPdf(input: PdfGenerationInput): Promise<Blob> {
    const html2pdf = (await import('html2pdf.js')).default;
    const htmlContent = this.buildPdfHtml(input);

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    try {
      const pdfBlob = await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `propuesta-firmada-${Date.now()}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(container)
        .outputPdf('blob');

      return pdfBlob as Blob;
    } finally {
      document.body.removeChild(container);
    }
  }

  private buildPdfHtml(input: PdfGenerationInput): string {
    const { title, content, acceptedByName, acceptedByEmail, acceptedAt, signatureData } = input;
    const date = new Date(acceptedAt).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    // v2 flat: content.blocks; v1 legacy: content.pages[].blocks
    let blocks: Block[] = [];
    if (Array.isArray(content?.blocks)) {
      blocks = content.blocks as Block[];
    } else if (Array.isArray(content?.pages)) {
      // Fallback best-effort: aplanar bloques de todas las páginas
      blocks = content.pages.flatMap((p: any) => p.blocks || []);
    }

    const blocksHtml = blocks.map(renderBlock).join('');

    const signatureHtml = signatureData
      ? `<div style="margin-top:16px;"><img src="${signatureData}" style="max-width:200px;max-height:80px;border:1px solid #e5e5e5;border-radius:4px;" /></div>`
      : '';

    return `
      <div style="font-family:'Inter',system-ui,sans-serif;color:#1a1a1a;max-width:700px;margin:0 auto;">
        <div style="display:flex;align-items:center;gap:12px;padding-bottom:20px;border-bottom:2px solid #0a0a0a;margin-bottom:24px;">
          <div style="width:36px;height:36px;background:#B6FF3C;border-radius:6px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:18px;color:#0a0a0a;">C</div>
          <div>
            <div style="font-size:16px;font-weight:700;">Cero Ideas</div>
            <div style="font-size:12px;color:#888;">Propuesta comercial</div>
          </div>
        </div>

        <h1 style="font-size:24px;font-weight:700;margin:0 0 8px;">${title}</h1>
        <p style="font-size:12px;color:#888;font-family:monospace;margin:0 0 32px;">Documento firmado · ${date}</p>

        ${blocksHtml}

        <div style="margin-top:40px;padding:24px;border:2px solid #22c55e;border-radius:8px;background:#f0fdf4;page-break-before:always;">
          <h2 style="font-size:16px;font-weight:700;color:#16a34a;margin:0 0 16px;">✓ Propuesta aceptada</h2>
          <table style="font-size:13px;border-collapse:collapse;">
            <tr><td style="padding:4px 16px 4px 0;color:#666;font-weight:600;">Firmante:</td><td>${acceptedByName}</td></tr>
            <tr><td style="padding:4px 16px 4px 0;color:#666;font-weight:600;">Email:</td><td>${acceptedByEmail}</td></tr>
            <tr><td style="padding:4px 16px 4px 0;color:#666;font-weight:600;">Fecha:</td><td>${date}</td></tr>
          </table>
          ${signatureHtml}
          <p style="font-size:10px;color:#888;margin-top:16px;border-top:1px solid #d1fae5;padding-top:12px;">
            Este documento constituye una firma electrónica simple conforme al Reglamento eIDAS (UE) 910/2014.
            La IP y user-agent del firmante han sido registrados en el sistema.
          </p>
        </div>

        <div style="margin-top:40px;padding-top:16px;border-top:1px solid #e5e5e5;font-size:10px;color:#888;text-align:center;">
          Generado por Cero Ideas CRM · ${new Date().toISOString().split('T')[0]}
        </div>
      </div>`;
  }
}

export const proposalPdfService = new ProposalPdfService();
